import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useMessage } from '../../../hooks/useMessage'
import { useSelector } from 'react-redux'
import Library from '../../../Library'
import DateInput from '../../../components/Inputs/DateInput'
import Textarea from '../../../components/Inputs/Textarea'
import ButtonsBlock from '../../../components/ButtonsBlock/ButtonsBlock'
import styled from 'styled-components'
import LinearLoader from '../../../components/Loaders/LinearLoader'
import moment from 'moment'
import Select from '../../../components/Inputs/Select'
import NumberInput from '../../../components/Inputs/NumberInput'
import TextInput from '../../../components/Inputs/TextInput'
import useWindowHeight from '../../../hooks/useWindowHeight'
import { useLanguage } from '../../../context/LanguageContext'

const Wrapper = styled.div`
  position: relative;
  overflow-y: auto;
  width: 100%;
  display: flex;
  flex-direction: column;
`

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: space-between;

  @media (max-width: 649px) { 
    max-width: 300px;
    margin: 35px auto 85px auto;
  }

  @media (min-width: 650px) and (max-width: 959px) {
    max-width: 610px;
    margin: 35px auto 60px auto;
  }

  @media (min-width: 960px) {
    max-width: 920px;
    margin: 35px 15px 10px 25px;
  }
`

const SectionWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  column-gap: 10px;
  row-gap: 30px;
  max-width: ${({ $maxWidth }) => $maxWidth}px;
  @media (max-width: 649px) {  
    row-gap: 30px;
  }
`

const RowWrapper = styled.div`
  display: flex;
  width: 300px;
  flex-wrap: nowrap;
  gap: 10px;
`

const Divider = styled.div`
  height: 1px;
  overflow: hidden;
  background-color: #e0e0e0;
  margin: 0 0 20px 0;

  @media (max-width: 649px) {
    margin: 20px 0 40px 0;
  }
`

const CommentWrapper = styled.div`
  width: 100%;
  @media (max-width: 649px) { 
    margin-bottom: 30px;
  }
`

export const Form = () => {
  const location = useLocation()
  const pageName = location.pathname.substring(1).split('/')[0]
  const params = useParams()
  const message = useMessage()
  const { language } = useLanguage()
  const isDataLoaded = useSelector((state) => state.loading.isDataLoaded)
  const wrapperRef = useRef(null)
  useWindowHeight(wrapperRef, 84)
  const debounceDelay = 500
  const { companyId } = useSelector(state => state.data)
  const [dataReady, setDataReady] = useState(false)
  const initialFormData = useMemo(() => ({
    id: params.id,
    date: '',
    car: '',
    mileage: null,
    inventory: '',
    supplier: '',
    sparePartCost: null,
    sparePartPayType: '',
    serviceProvider: '',
    completedWorksList: '',
    workCost: null,
    workPayType: '',
    comment: '',
  }), [params.id])
  const [form, setForm] = useState(initialFormData)

  const { cars, suppliers, inventory, maintenance, loading, error } = useSelector(state => state.data)

  useEffect(() => {
    if (isDataLoaded && !loading && !error) {
      if (params.id && maintenance.length) {
        const formData = Library.findItemById(maintenance, params.id)
        if (formData) {
          setForm(formData)
        } else {
          setForm(initialFormData)
        }
      }
      setDataReady(true)
    }
  }, [initialFormData, isDataLoaded, loading, error, maintenance, params.id])

  const paymentTypeSelectOptions = [
    {
      value: '',
      label: '-',
      disabled: false,
      defaultValue: true,
    },
    { value: 'Ндс', label: 'Ндс' },
    { value: 'Нал', label: 'Нал' },
    { value: 'Без ндс', label: 'Без ндс' },
  ]

  function getSelectOptions(data) {
    let arr = [
      {
        value: '',
        label: '-',
        defaultValue: true,
        disabled: false,
      },
    ]
    if (data && data.length) {
      data.forEach((item) => {
        arr.push({ value: item.id, label: item.name })
      })
    }
    return arr
  }

  function getInventorySelectOptions(data) {

    const getLabel = (item) => {
      let str = item.name
      if (item.partNumber) {
        str += ` - ${item.partNumber}`
      }
      if (item.manufacturer) {
        str += ` (${item.manufacturer})`
      }
      return str
    }

    let arr = [
      {
        value: '',
        label: '-',
        defaultValue: true,
        disabled: false,
      },
    ]
    if (data && data.length) {
      data.forEach((item) => {
        arr.push({ value: item.id, label: getLabel(item) })
      })
    }
    return arr
  }

  //------- CHANGE HISTORY -------//

  const keyTranslation = useMemo(() => [
    { id: 'id', name: 'id' },
    { id: 'timestamp', name: 'timestamp' },
    { id: 'status', name: 'Статус' },
    { id: 'date', name: 'Дата' },
    { id: 'car', name: 'Автомобиль' },
    { id: 'mileage', name: 'Пробег' },
    { id: 'inventory', name: 'Номенклатура' },
    { id: 'supplier', name: 'Поставщик/исполнитель' },
    { id: 'sparePartCost', name: 'Стоимость детали' },
    { id: 'sparePartPayType', name: 'Форма оплаты' },
    { id: 'serviceProvider', name: 'Исполнитель' },
    { id: 'completedWorksList', name: 'Выполненные работы' },
    { id: 'workCost', name: 'Стоимость работ' },
    { id: 'workPayType', name: 'Форма оплаты' },
    { id: 'comment', name: 'Комментарий' },
    { id: 'lastModifiedBy', name: 'Кто изменил' },
  ], [])

  const getKeyTranslation = keyTranslation.reduce((obj, item) => {
    obj[item.id] = item.name
    return obj
  }, {})

  const formatValue = (key, value) => {
    switch (key) {
      case 'status': {
        return value === 'active' ? 'активный' : 'помечен на удаление'
      }
      case 'inventory': {
        return Library.findNameByKey(inventory, value)
      }
      case 'serviceProvider':
      case 'supplier': {
        return Library.findNameByKey(suppliers, value)
      }
      case 'car': {
        return Library.findNameByKey(cars, value)
      }
      case 'date': {
        return Library.formatDate(value, 'DD.MM.yyyy')
      }
      default: {
        return value
      }
    }
  }

  const renderChangeDetails = (change) => {
    if (!change.newData) {
      return ''
    }

    const parseJSON = (data) => {
      try {
        return JSON.parse(data.replace(/\n/g, '\\n'))
      } catch (e) {
        console.error("Error parsing JSON: ", e)
        return {}
      }
    }

    const isValueEmpty = (value) => {
      return value === '' || value === null || value === 0
    }

    const isValueChanged = (oldValue, newValue) => {
      return oldValue !== newValue
    }

    const parsedNewData = parseJSON(change.newData.replace(/\n/g, '\\n'))
    const parsedOldData = change.oldData ? parseJSON(change.oldData.replace(/\n/g, '\\n')) : {}

    return Object.keys(parsedNewData).map((key) => {
      let oldData = parsedOldData[key] ? formatValue(key, parsedOldData[key]) : ''
      let newData = parsedNewData[key] ? formatValue(key, parsedNewData[key]) : ''

      if ((isValueEmpty(oldData) && isValueEmpty(newData)) || !isValueChanged(oldData, newData)) {
        return null
      }

      return (
        <div key={key} style={{ marginBottom: '10px' }}>
          <span>{getKeyTranslation[key] || key}:</span>
          {'\u00A0'}
          <span>{oldData ? oldData : <span style={{ color: '#c3c3c3' }}>{language === 'ru' ? 'Пустое значение' : 'Blank value'}</span>}</span>
          {'\u00A0\u2192\u00A0'}
          <span>{newData ? newData : <span style={{ color: '#c3c3c3' }}>{language === 'ru' ? 'Пустое значение' : 'Blank value'}</span>}</span>
        </div>
      )
    }).filter(Boolean)
  }

  const [partNumber, setPartNumber] = useState('')
  const [manufacturer, setManufacturer] = useState('')

  useEffect(() => {
    if (!form.inventory) return
    const item = Library.findItemById(inventory, form.inventory)
    if (item) {
      setPartNumber(item.partNumber)
      setManufacturer(item.manufacturer)
    }
  }, [form.inventory, inventory])

  return (
    <Wrapper ref={wrapperRef}>
      <LinearLoader
        isLoading={!dataReady || !isDataLoaded}
        style={{ height: '4px' }}
      />
      {isDataLoaded && dataReady &&
        <FormWrapper>
          <SectionWrapper $maxWidth={1000}>
            <RowWrapper>
              {/* ------- DATE ------- */}
              <DateInput
                label='Дата'
                name='date'
                id='date'
                value={form.date ? moment(form.date).format('yyyy-MM-DD') : ''}
                onChange={(e) => {
                  Library.changeHandler(e, form, setForm)
                }}
              />
            </RowWrapper>
            <RowWrapper>
              {/* ------- CAR ------- */}
              < Select
                label='Автомобиль'
                id='car'
                name='car'
                value={form.car}
                options={getSelectOptions(cars)}
                searchable={true}
                disabled={false}
                onChange={(e) => {
                  Library.changeHandler(e, form, setForm)
                }}
              />
            </RowWrapper>
            <RowWrapper>
              {/* ------- MILEAGE ------- */}
              <NumberInput
                label='Пробег'
                name='mileage'
                id='mileage'
                value={form.mileage === null ? '' : form.mileage}
                onChange={(e) => {
                  Library.changeHandler(e, form, setForm)
                }}
                debounceDelay={debounceDelay}
                readOnly={false}
              />
            </RowWrapper>
            <RowWrapper>
              {/* ------- INVENTORY ------- */}
              < Select
                label='Номенклатура'
                id='inventory'
                name='inventory'
                value={form.inventory}
                options={getInventorySelectOptions(inventory)}
                searchable={true}
                disabled={false}
                onChange={(e) => {
                  Library.changeHandler(e, form, setForm)
                }}
              />
            </RowWrapper>
            {/* ------- PART NUMBER ------- */}
            <RowWrapper>
              <TextInput
                label='Номер детали'
                name='partNumber'
                id='partNumber'
                value={partNumber}
                isClearable={true}
                debounceDelay={debounceDelay}
                readOnly={true}
                disabled={false}
              />
            </RowWrapper>
            {/* ------- MANUFACTURER ------- */}
            <RowWrapper>
              <TextInput
                label='Производитель'
                name='manufacturer'
                id='manufacturer'
                value={manufacturer}
                isClearable={true}
                debounceDelay={debounceDelay}
                readOnly={true}
              />
            </RowWrapper>
            <RowWrapper>
              {/* ------- SUPPLIER ------- */}
              < Select
                label={'Поставщик'}
                id='supplier'
                name='supplier'
                value={form.supplier}
                options={getSelectOptions(suppliers)}
                searchable={true}
                disabled={false}
                onChange={(e) => {
                  Library.changeHandler(e, form, setForm)
                }}
              />
            </RowWrapper>
            <RowWrapper>
              {/* ------- SPARE PART COST ------- */}
              <NumberInput
                label='Стоимость детали'
                name='sparePartCost'
                id='sparePartCost'
                value={form.sparePartCost === null ? '' : form.sparePartCost}
                onChange={(e) => {
                  Library.changeHandler(e, form, setForm)
                }}
                debounceDelay={debounceDelay}
                readOnly={false}
              />
              {/* ------- SPARE PART PAY TYPE ------- */}
              <Select
                label='Форма оплаты детали'
                name='sparePartPayType'
                id='sparePartPayType'
                value={form.sparePartPayType}
                onChange={(e) => {
                  Library.changeHandler(e, form, setForm)
                }}
                options={paymentTypeSelectOptions}
              />
            </RowWrapper>
            <RowWrapper>
              {/* ------- SERVICE PROVIDER ------- */}
              < Select
                label={'Исполнитель'}
                id='serviceProvider'
                name='serviceProvider'
                value={form.serviceProvider}
                options={getSelectOptions(suppliers)}
                searchable={true}
                disabled={false}
                onChange={(e) => {
                  Library.changeHandler(e, form, setForm)
                }}
              />
            </RowWrapper>
            <RowWrapper>
              {/* ------- COMPLETED WORKS LIST ------- */}
              <TextInput
                label='Выполненные работы'
                id='completedWorksList'
                name='completedWorksList'
                value={form.completedWorksList}
                debounceDelay={debounceDelay}
                isClearable={false}
                disabled={false}
                onChange={(e) => {
                  Library.changeHandler(e, form, setForm)
                }}
              />
            </RowWrapper>
            <RowWrapper>
              {/* ------- WORK COST ------- */}
              <NumberInput
                label='Стоимость работ'
                name='workCost'
                id='workCost'
                value={form.workCost === null ? '' : form.workCost}
                onChange={(e) => {
                  Library.changeHandler(e, form, setForm)
                }}
                debounceDelay={debounceDelay}
                readOnly={false}
              />
              {/* ------- WORK PAY TYPE ------- */}
              <Select
                label='Форма оплаты работ'
                name='workPayType'
                id='workPayType'
                value={form.workPayType}
                onChange={(e) => {
                  Library.changeHandler(e, form, setForm)
                }}
                options={paymentTypeSelectOptions}
              />
            </RowWrapper>
          </SectionWrapper>
          <Divider />
          {/* ------- COMMENT ------- */}
          <CommentWrapper>
            <Textarea
              label='Комментарий'
              name='comment'
              id='comment'
              value={form.comment}
              debounceDelay={debounceDelay}
              onChange={(e) => {
                Library.changeHandler(e, form, setForm)
              }}
            />
          </CommentWrapper>
          <ButtonsBlock
            handler={Library.handler}
            form={form}
            edit={params.id ? true : false}
            label={form.date ? moment(form.date).format('DD.MM.yyyy') : ''}
            message={message}
            fileFolder={pageName}
            path='data'
            request={pageName}
            changeHistoryTableName={`${companyId}_change_history`}
            changeHistoryId={form.id}
            renderChangeDetails={renderChangeDetails}
          />
        </FormWrapper>
      }
    </Wrapper>
  )
}
